import { createWebSupportArticleEventFactory } from '@spotify-internal/ubi-sdk-support-spotify-com-web-support-article';
import { useMemo } from 'react';

import { PageId } from 'utils/ubi/pageId';

import { useUbiPage } from '../useUbiPage';
import { useUbiContext } from '../ubiProvider';

const pageId = PageId.Article;

const isInternalLink = (uri: string) => uri.startsWith('/') || uri.includes('spotify.com');

export const useUbiArticlePage = (pageArticleSlug: string) => {
  const ubiLogger = useUbiContext();
  const { pageFactory } = useUbiPage(pageId, pageArticleSlug, createWebSupportArticleEventFactory);
  const articleContainer = useMemo(
    () =>
      pageFactory
        .categoryArticleContainerFactory({ identifier: pageArticleSlug })
        .articleContainerFactory({ identifier: pageArticleSlug }),
    [pageArticleSlug, pageFactory],
  );

  return useMemo(
    () => ({
      logArticleImpression: () => {
        ubiLogger.logImpression(articleContainer.impression());
      },
      logBreadcrumbHomeLink: () =>
        pageFactory
          .categoryArticleContainerFactory({ identifier: pageArticleSlug })
          .breadcrumbsFactory()
          .homeLinkFactory(),
      logBreadcrumbLink: (parentCategorySlug: string) =>
        pageFactory
          .categoryArticleContainerFactory({ identifier: pageArticleSlug })
          .breadcrumbsFactory()
          .parentCategoryBreadcrumbFactory({ identifier: parentCategorySlug }),
      logRelatedArticle: (slug: string, position: number) =>
        pageFactory.categoryArticleContainerFactory({ identifier: pageArticleSlug }).relatedArticleFactory({
          identifier: slug,
          position,
        }),
      logArticleLink: (slug: string) => articleContainer.articleLinkFactory({ identifier: slug }),
      logVideoImpression: (videoId: string) =>
        ubiLogger.logImpression(articleContainer.videoElementFactory({ identifier: videoId }).impression()),
      logAccordionImpression: (key: string) =>
        ubiLogger.logImpression(articleContainer.accordionFactory({ identifier: key }).impression()),
      logAccordionInteraction: (key: string, expanded: boolean) => {
        if (expanded) {
          ubiLogger.logInteraction(articleContainer.accordionFactory({ identifier: key }).hitUiReveal());
        } else {
          ubiLogger.logInteraction(articleContainer.accordionFactory({ identifier: key }).hitUiHide());
        }
      },
      logLinkImpression: (uri: string) => {
        const urlLinkInstance = articleContainer.urlLinkFactory({ uri });
        if (isInternalLink(uri)) {
          ubiLogger.logImpression(urlLinkInstance.hitUiNavigate({ destination: uri }));
        } else {
          ubiLogger.logImpression(urlLinkInstance.hitNavigateToExternalUri({ destination: uri }));
        }
      },
      logLinkInteraction: (uri: string) => {
        const urlLinkInstance = articleContainer.urlLinkFactory({ uri });
        if (isInternalLink(uri)) {
          ubiLogger.logInteraction(urlLinkInstance.hitUiNavigate({ destination: uri }));
        } else {
          ubiLogger.logInteraction(urlLinkInstance.hitNavigateToExternalUri({ destination: uri }));
        }
      },
      logArticleHelpfulImpression: () => {
        ubiLogger.logImpression(
          pageFactory
            .categoryArticleContainerFactory({ identifier: pageArticleSlug })
            .articleHelpfulContainerFactory()
            .impression(),
        );
      },
      logArticleHelpfulYes: () => {
        ubiLogger.logInteraction(
          pageFactory
            .categoryArticleContainerFactory({ identifier: pageArticleSlug })
            .articleHelpfulContainerFactory()
            .articleHelpfulYesButtonFactory()
            .hitUiSelect(),
        );
      },
      logArticleHelpfulNo: () => {
        ubiLogger.logInteraction(
          pageFactory
            .categoryArticleContainerFactory({ identifier: pageArticleSlug })
            .articleHelpfulContainerFactory()
            .articleHelpfulNoButtonFactory()
            .hitUiSelect(),
        );
      },
      logCTAImpression: (identifier: string) => {
        ubiLogger.logImpression(
          pageFactory
            .categoryArticleContainerFactory({ identifier: pageArticleSlug })
            .articleCtaFactory({ identifier })
            .impression(),
        );
      },
      logCTALinkInteraction: (destination: string, identifier: string) => {
        const urlLinkInstance = pageFactory
          .categoryArticleContainerFactory({ identifier: pageArticleSlug })
          .articleCtaFactory({ identifier });
        ubiLogger.logInteraction(urlLinkInstance.hitUiNavigate({ destination }));
      },
    }),
    [ubiLogger, articleContainer, pageFactory, pageArticleSlug],
  );
};

export type UbiArticlePage = ReturnType<typeof useUbiArticlePage>;
